import React, { useState } from "react";
import { Alert, Button, Collapse, DatePicker, Form, Input, Select, Spin, Table, Tag, Tooltip } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useLazyFetchStockSummaryReportQuery } from "features/store";

const { Panel } = Collapse;

const MATERIAL_TYPES = ["CLOTH MATERIAL", "CAP MATERIAL", "OTHER MATERIAL"];

const columns = [
  { title: "Material Type", dataIndex: "material_type", key: "material_type" },
  { title: "Material Name", dataIndex: "material_name", key: "material_name" },
  { title: "Color", dataIndex: "color", key: "color" },
  { title: "Sub Type", dataIndex: "sub_type", key: "sub_type" },
  { title: "Current Qty", dataIndex: "current_quantity", key: "current_quantity" },
  { title: "Previous Qty", dataIndex: "previous_quantity", key: "previous_quantity" },
  { title: "Unit", dataIndex: "unit", key: "unit" },
  { title: "Bill No", dataIndex: "bill_number", key: "bill_number" },
  { title: "Challan No", dataIndex: "challan_number", key: "challan_number" },
  { title: "Last Updated", dataIndex: "last_updated", key: "last_updated" },
  { title: "Updated By", dataIndex: "updated_by", key: "updated_by" },
];

const csvHeaders = [
  { label: "Material Type", key: "material_type" },
  { label: "Material Name", key: "material_name" },
  { label: "Color", key: "color" },
  { label: "Sub Type", key: "sub_type" },
  { label: "Current Qty", key: "current_quantity" },
  { label: "Previous Qty", key: "previous_quantity" },
  { label: "Unit", key: "unit" },
  { label: "Bill No", key: "bill_number" },
  { label: "Challan No", key: "challan_number" },
  { label: "Last Updated", key: "last_updated" },
  { label: "Updated By", key: "updated_by" },
];

const InvRepStockSummary = () => {
  const [form] = Form.useForm();
  const [fieldErrors, setFieldErrors] = useState({});
  const [trigger, { data, isLoading, isFetching, error }] = useLazyFetchStockSummaryReportQuery();

  const handleApply = async () => {
    setFieldErrors({});
    const v = form.getFieldsValue();
    const params = {};
    if (v.type) params.type = v.type;
    if (v.cloth_type) params.cloth_type = v.cloth_type;
    if (v.cap_material) params.cap_material = v.cap_material;
    if (v.color) params.color = v.color;
    if (v.date_from) params.date_from = v.date_from.format("YYYY-MM-DD");
    if (v.date_to) params.date_to = v.date_to.format("YYYY-MM-DD");
    if (v.created_from) params.created_from = v.created_from.format("YYYY-MM-DD");
    if (v.created_to) params.created_to = v.created_to.format("YYYY-MM-DD");
    if (v.bill_number) params.bill_number = v.bill_number;
    if (v.challan_number) params.challan_number = v.challan_number;
    if (v.updated_by) params.updated_by = v.updated_by;
    if (v.created_by) params.created_by = v.created_by;
    const result = await trigger(params);
    if (result?.error?.status === 400 && result.error.data?.errors) {
      setFieldErrors(result.error.data.errors);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setFieldErrors({});
    trigger({});
  };

  const results = data?.results || [];
  const count = data?.count ?? results.length;

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginBottom: 16 }}>Stock Summary Report</h2>

      <Collapse defaultActiveKey={["filters"]} style={{ marginBottom: 16 }}>
        <Panel header="Filters" key="filters">
          <Form form={form} layout="inline" style={{ flexWrap: "wrap", gap: "8px 0" }}>
            <Form.Item name="type" label="Material Type">
              <Select allowClear placeholder="All types" style={{ width: 180 }}>
                {MATERIAL_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="cloth_type" label="Cloth Type">
              <Input placeholder="e.g. TWILL" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="cap_material" label="Cap Material">
              <Input placeholder="Cap material" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="color" label="Color">
              <Input placeholder="Color" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="date_from" label="Date From">
              <DatePicker />
            </Form.Item>
            <Form.Item name="date_to" label="Date To">
              <DatePicker />
            </Form.Item>
            <Form.Item name="bill_number" label="Bill No">
              <Input placeholder="Bill number" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="challan_number" label="Challan No">
              <Input placeholder="Challan number" style={{ width: 130 }} />
            </Form.Item>
            <Form.Item
              name="updated_by"
              label="Updated By"
              validateStatus={fieldErrors.updated_by ? "error" : ""}
              help={fieldErrors.updated_by}
            >
              <Input placeholder="Username" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="created_by"
              label="Created By"
              validateStatus={fieldErrors.created_by ? "error" : ""}
              help={fieldErrors.created_by}
            >
              <Input placeholder="Username" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="created_from"
              label="Created From"
              validateStatus={fieldErrors.created_from ? "error" : ""}
              help={fieldErrors.created_from}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              name="created_to"
              label="Created To"
              validateStatus={fieldErrors.created_to ? "error" : ""}
              help={fieldErrors.created_to}
            >
              <DatePicker />
            </Form.Item>
          </Form>
          {error?.status === 400 && !error?.data?.errors && (
            <Alert type="error" message={error?.data?.detail || "Invalid filter values."} style={{ marginTop: 8 }} />
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleApply} loading={isLoading || isFetching}>
              Apply Filters
            </Button>
            <Button onClick={handleClear}>Clear Filters</Button>
          </div>
        </Panel>
      </Collapse>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        {data && (
          <Tag color="blue" style={{ fontSize: 14 }}>Total items: {count}</Tag>
        )}
        {results.length > 0 && (
          <Tooltip title="Export to Excel">
            <CSVLink
              data={results}
              headers={csvHeaders}
              filename="stock-summary-report.csv"
            >
              <Button icon={<FileExcelOutlined />} style={{ color: "#217346", borderColor: "#217346" }}>
                Export to Excel
              </Button>
            </CSVLink>
          </Tooltip>
        )}
      </div>

      <Spin spinning={isLoading || isFetching}>
        <Table
          columns={columns}
          dataSource={results}
          rowKey={(r, i) => r.id ?? i}
          locale={{ emptyText: "No records found" }}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Spin>
    </div>
  );
};

export default InvRepStockSummary;
